import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { inboxAPI } from "../../api/client";
import { INBOX_CHANGED_EVENT } from "../../hooks/useInboxUnreadCount";
import { apiError, formatTimestamp, initials } from "./taskUtils";
import "./Tasks.css";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
];

function announce(unreadCount) {
  window.dispatchEvent(
    new CustomEvent(INBOX_CHANGED_EVENT, { detail: { unreadCount } }),
  );
}

function describe(item) {
  if (item.type === "TASK_ASSIGNED") return "assigned you a task";
  if (item.type === "TASK_MENTION") return "mentioned you in a comment";
  if (item.type === "TASK_APPROVAL_REQUESTED") return "requested your approval";
  if (item.type === "TASK_APPROVAL_DECISION") {
    return (
      {
        approved: "approved your request",
        changes_requested: "requested changes on your request",
        rejected: "rejected your request",
      }[item.data?.decision] || "decided on your approval request"
    );
  }
  return "sent you an update";
}

// The Inbox tab of My Tasks: assignments and @mentions for the current user.
export default function InboxPanel() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const resp = await inboxAPI.list(filter);
      setItems(resp.data.items || []);
      setUnreadCount(resp.data.unreadCount || 0);
      announce(resp.data.unreadCount || 0);
      setError("");
    } catch (err) {
      setError(apiError(err, "Could not load your inbox"));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const setRead = async (item, read) => {
    setItems((prev) =>
      prev.map((n) =>
        n.id === item.id
          ? { ...n, readAt: read ? new Date().toISOString() : null }
          : n,
      ),
    );
    try {
      const resp = read
        ? await inboxAPI.markRead(item.id)
        : await inboxAPI.markUnread(item.id);
      setUnreadCount(resp.data.unreadCount);
      announce(resp.data.unreadCount);
    } catch (err) {
      setError(apiError(err));
      load();
    }
  };

  const markAllRead = async () => {
    try {
      await inboxAPI.markAllRead();
      announce(0);
      load();
    } catch (err) {
      setError(apiError(err));
    }
  };

  const open = (item) => {
    if (!item.readAt) setRead(item, true);
    if (item.task) navigate(`/tasks/${item.task.id}`);
  };

  return (
    <div className="tk-list-wrap">
      <div className="tk-tasks-toolbar">
        <div
          className="tk-scope-pills"
          role="tablist"
          aria-label="Filter inbox"
        >
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              role="tab"
              aria-selected={filter === f.key}
              className={filter === f.key ? "active" : ""}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              {f.key === "unread" && unreadCount > 0 && (
                <span className="tk-tab-count">{unreadCount}</span>
              )}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="btn-secondary btn-sm"
          onClick={markAllRead}
          disabled={unreadCount === 0}
        >
          Mark all as read
        </button>
      </div>
      {error && <p className="tk-error">{error}</p>}
      <div className="tk-inbox">
        {loading && <p className="tk-muted tk-list-msg">Loading…</p>}
        {!loading && items.length === 0 && (
          <div className="tk-empty">
            <p>
              {filter === "unread"
                ? "You're all caught up."
                : "Nothing here yet. When someone assigns you a task or @mentions you, it shows up here."}
            </p>
          </div>
        )}
        {items.map((item) => (
          <div
            key={item.id}
            role="button"
            tabIndex={0}
            className={`tk-inbox-item ${item.readAt ? "" : "unread"}`.trim()}
            onClick={() => open(item)}
            onKeyDown={(e) => e.key === "Enter" && open(item)}
          >
            <span className="tk-inbox-dot" aria-hidden="true" />
            <span className="tk-avatar">{initials(item.actor?.name)}</span>
            <div className="tk-inbox-main">
              <div className="tk-inbox-line">
                <strong>{item.actor?.name || "Someone"}</strong>{" "}
                {describe(item)}
                <span className="tk-muted">
                  {" "}
                  · {formatTimestamp(item.createdAt)}
                </span>
              </div>
              <div
                className={`tk-inbox-task ${item.task?.completed ? "tk-done-text" : ""}`.trim()}
              >
                {item.task?.name || item.data?.taskName}
              </div>
              {item.data?.snippet && (
                <div className="tk-inbox-snippet">{item.data.snippet}</div>
              )}
            </div>
            <button
              type="button"
              className="tk-link-btn tk-inbox-toggle"
              onClick={(e) => {
                e.stopPropagation();
                setRead(item, !item.readAt);
              }}
            >
              {item.readAt ? "Mark unread" : "Mark read"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
